import { createTranslator } from "next-intl"

import { type Locale } from "@/i18n/config"

/**
 * Translating from code that is not a React component.
 *
 * ── Why this has to exist ────────────────────────────────────────────────────────────────
 *
 * `useTranslations` is a hook, so it reaches anything rendered inside the provider and nothing
 * else. The toasts are the case that breaks it: `toast(MESSAGES.auth.signedOut)` is called from
 * 59 places, and a third of them are plain modules — `lib/bookmarks`, `lib/execute_search`,
 * `lib/projects`, `lib/utils` — which have no component, no render, and nowhere to hold a hook.
 *
 * Threading a translator through those call sites is not available either: they are library
 * functions called from event handlers and promises, and passing `t` into each would put a
 * localisation concern into the signature of every one of them.
 *
 * So the messages are mirrored here, into module scope, by a component that DOES sit inside the
 * provider (see components/providers/ImperativeMessages). Anything may then translate at any time.
 *
 * ── What this costs, stated plainly ─────────────────────────────────────────────────────
 *
 * It is module-level mutable state, which on the server is shared across requests. That is safe
 * for exactly one reason: only the client ever writes it, and a browser tab has one reader and
 * one language. Do NOT use this on the server — server components have `getTranslations`, which
 * is per-request and correct. This is for imperative CLIENT code and nothing else.
 *
 * Before the mirror is populated a key returns itself, which is what next-intl does for a
 * missing message: visible in the UI, never a crash. In practice a toast cannot fire before the
 * tree has rendered, which is what makes that acceptable rather than merely unlikely.
 */
let current: { locale: Locale; messages: Record<string, unknown> } | null = null

/** Called by the mirroring component whenever the locale or the messages change. */
export function setImperativeMessages(locale: Locale, messages: Record<string, unknown>) {
  current = { locale, messages }
}

/** Translate a dotted key from outside React. */
export function translate(key: string, values?: Record<string, string | number | Date>): string {
  if (!current) return key
  try {
    const t = createTranslator({ locale: current.locale, messages: current.messages })
    return t(key as never, values as never) as string
  } catch {
    return key
  }
}
